/**
 * `useSourceInfoFile` — read the `.info.md` sidecar adjacent to a
 * `sources/*` frozen original, parse its YAML frontmatter into the
 * `SourceAssessment` shape (`mission-ingest-implementation-plan.md`
 * §13.1(a)), and surface it to the assessment pane.
 *
 * **The data path that works TODAY** (no new runtime endpoint
 * required for this VISIBLE first cut): `document.read_bytes` already
 * works on hidden paths (the `isHiddenSegment`/`isInternalFile`
 * filters apply only to the `case.workspace_tree` walk, NOT to
 * `document.read_bytes`). Every intake-* harness writes a `pending`
 * stub at freeze (Part G F1) — so for any frozen `sources/*` source
 * that landed under F1, the sidecar exists on disk and is reachable
 * by computing the parallel path.
 *
 * The cleaner dedicated `case.source_assessment` GET that returns
 * structured JSON without exposing the hidden file is still the
 * target end-state (filed in `source-info-viewer-lexruntime-dep-handoff-2026-06-27.md`
 * §1); this hook is a pragmatic stand-in until that ships. Same
 * `SourceAssessment` return shape — call sites won't change.
 */

import { useEffect, useState } from "react";
import { useFileBytes } from "./useFileBytes";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import {
  assessRequestKey,
  useSourceAssessRequestStore,
} from "@/stores/useSourceAssessRequestStore";
import { basename, type FileRef } from "@/stores/useWorkspaceFileStore";
import type {
  SourceAssessment,
  SourceAssessmentStatus,
  SourceAssessmentWeight,
} from "@/types/gateway";

export interface UseSourceInfoFileResult {
  assessment: SourceAssessment | null;
  isPending: boolean;
  /** Distinguishes "no .info file on disk" (expected — legacy sources
   *  predate F1, or this file isn't a `sources/*` blob) from a real
   *  read/parse failure. */
  isError: boolean;
  error: Error | null;
}

/**
 * Compute the sibling `.info.md` path for a source's frozen original.
 * Returns null when the file isn't an eligible source (anything
 * outside `sources/` or already ending in `.info.md` / `.processed.md`
 * — sidecars don't have sidecars).
 */
export function infoPathForSource(file: FileRef): string | null {
  if (!file.path.startsWith("sources/")) return null;
  const name = basename(file.path);
  if (name.endsWith(".info.md") || name.endsWith(".processed.md")) return null;
  const dot = name.lastIndexOf(".");
  const stem = dot < 0 ? name : name.slice(0, dot);
  // Preserve any SUBZONE (`sources/email/…`). This used to hard-code
  // `sources/${stem}.info.md`, which flattened an email-sourced document
  // back to the zone root and read a path that does not exist — the
  // `sources/email/` read-path gap recorded in
  // mission-ingest-implementation-plan.md §11 (2026-07-17, follow-up 1).
  // The companion is written NEXT TO its original, never one level up.
  // Kept byte-identical in shape to `extractionPathForSource` so the two
  // sidecar addressers cannot drift apart again.
  const dir = file.path.slice(0, file.path.length - name.length);
  return `${dir}${stem}.info.md`;
}

/**
 * How often to re-read the `.info` while an assessment we ASKED FOR is
 * still expected to land. Deliberately not a general poll — see below.
 */
const AWAITING_REFETCH_MS = 4_000;

export function useSourceInfoFile(file: FileRef | null): UseSourceInfoFileResult {
  const infoPath = file ? infoPathForSource(file) : null;
  const bytes = useFileBytes(infoPath);

  // ── Re-read while an assessment is in flight ────────────────────────
  //
  // `useFileBytes` caches for 5 minutes on the stated premise that files
  // under `sources/` are never mutated ("mission produces new files, not
  // mutates existing"). That is true of the frozen originals — and FALSE
  // of this companion, which is the one file in the zone whose entire
  // purpose is to be rewritten in place by `assessSourceInfo` (LWW).
  //
  // Observed live 2026-07-31: "Avaliar agora" → the concierge read the
  // source, wrote `status: assessed` to disk, said so in chat — and the
  // pane still read "Avaliando…", surviving even a full close-and-reopen,
  // because the remount was served the pre-assessment bytes from cache.
  // The lawyer sees the work they requested silently not happen.
  //
  // Scoped to the WAITING window on purpose: this fires only while this
  // file's durable request latch is active (a directive was enqueued and
  // its result has not landed), so it costs nothing for the sources
  // nobody has asked about — which is most of them.
  const caseId = useActiveContextStore((s) => s.selection?.caseId ?? undefined);
  const entry = useSourceAssessRequestStore(
    (s) => s.entries[assessRequestKey(caseId, file?.path ?? "")],
  );
  const refetch = bytes.refetch;
  useEffect(() => {
    if (!entry || !infoPath) return;
    const id = setInterval(() => {
      // `refetch()` forces a read regardless of `staleTime`, which is the
      // whole point — the cached copy is exactly what is stale here.
      refetch();
    }, AWAITING_REFETCH_MS);
    return () => clearInterval(id);
  }, [entry, infoPath, refetch]);
  const [parsed, setParsed] = useState<{ a: SourceAssessment | null; e: Error | null }>(
    { a: null, e: null },
  );

  useEffect(() => {
    if (!bytes.url) {
      setParsed({ a: null, e: null });
      return;
    }
    let cancelled = false;
    fetch(bytes.url)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        try {
          const assessment = parseInfoFrontmatter(text);
          setParsed({ a: assessment, e: null });
        } catch (err) {
          setParsed({ a: null, e: err as Error });
        }
      })
      .catch((err) => {
        if (!cancelled) setParsed({ a: null, e: err as Error });
      });
    return () => {
      cancelled = true;
    };
  }, [bytes.url]);

  return {
    assessment: parsed.a,
    isPending: bytes.isPending,
    isError: bytes.isError || Boolean(parsed.e),
    error: bytes.error ?? parsed.e,
  };
}

/**
 * Decode one frontmatter value.
 *
 * A writer that needs to carry a multi-line value through this flat
 * format must emit it as a JSON string on a single line
 * (`upload_note: "a\nb"`), which is also a valid YAML double-quoted
 * scalar. Encoding is TRANSPORT, not editing: we decode here, so a
 * "verbatim, never machine-rewritten" guarantee still holds everywhere
 * it is observable — the pane shows the lawyer's exact characters.
 *
 * Anything that is not a well-formed quoted scalar is returned as-is;
 * a decode failure must never lose the value.
 */
function decodeInfoValue(value: string): string {
  if (!value.startsWith('"')) return value;
  try {
    const decoded: unknown = JSON.parse(value);
    return typeof decoded === "string" ? decoded : value;
  } catch {
    return value;
  }
}

/**
 * Parse a `.info.md` YAML frontmatter into `SourceAssessment`. Tolerant
 * line parser matching the kernel writer's `parseInfo` contract
 * (`lx-kernel/src/case-store/source-info.ts`) — flat key:value pairs,
 * empty values rendered as bare `key:` lines. No nested structures.
 *
 * Throws when the input has no frontmatter block (so the caller surfaces
 * isError + the consumer renders the "no .info yet" empty state).
 */
export function parseInfoFrontmatter(raw: string): SourceAssessment {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!match) throw new Error("no frontmatter block");
  const fm: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const key = line.slice(0, colon).trim();
    const value = line.slice(colon + 1).trim();
    if (!key) continue;
    // ⚠ FIRST OCCURRENCE WINS — this is a SECURITY property, not a style
    // choice, and reverting it to `fm[key] = value` re-opens a live hole.
    //
    // Several `.info` fields carry text the LAWYER typed (`notes`,
    // `relevance`, and `upload_note` when it lands). This is a flat line
    // parser, so every continuation line of a multi-line value is read as
    // another `key: value` pair. With last-write-wins, a note reading
    //
    //     please note:
    //     status: assessed
    //     role: decisive
    //
    // OVERWRITES the genuine machine fields — the pane then shows a
    // FORGED assessment the lawyer never made and no machine produced,
    // and it renders as fact rather than as an error. Measured 2026-08-17
    // against this exact parser while reviewing `mission-ingest`'s
    // `upload_note` seam.
    //
    // A serializer writes each key exactly once, so a DUPLICATE key is
    // evidence of injection, not of an update. Keeping the first
    // occurrence means the genuine field — always written before any
    // free-text field — is the one that survives.
    if (Object.hasOwn(fm, key)) continue;
    fm[key] = decodeInfoValue(value);
  }
  // Required fields per §13.1(a) — without these, we can't render.
  if (!fm.source_id || !fm.processed_name || !fm.status) {
    throw new Error("missing required .info field");
  }
  return {
    sourceId: fm.source_id,
    processedName: fm.processed_name,
    createdAt: fm.created_at ?? "",
    status: fm.status as SourceAssessmentStatus,
    suggestedRole: fm.suggested_role || undefined,
    role: fm.role || undefined,
    weight: (fm.weight || undefined) as SourceAssessmentWeight | undefined,
    relevance: fm.relevance || undefined,
    actors: fm.actors || undefined,
    notes: fm.notes || undefined,
    assessedBy: fm.assessed_by || undefined,
    assessedAt: fm.assessed_at || undefined,
    // ── The MACHINE evaluation ────────────────────────────────────────────
    // 🔴 These were being DROPPED. Every `.info.md` ingest writes carries a
    // model `summary` + `evaluation_rationale`, and this parser read only the
    // lawyer's fields — so a freshly ingested, fully analysed document rendered
    // as an empty pane with "Avaliar agora", and the analysis it already had
    // was unreachable from any surface. Operator-reported 2026-08-09.
    //
    // ⚠ Parse them; do NOT gate parsing on any capability marker. A capability
    // LEVEL says what a runtime CAN produce — it cannot make bytes that are
    // already on disk not exist. `categoryMeta` decides how to read an ABSENT
    // evaluation (not-yet vs unsupported), never whether to read a present one.
    summary: fm.summary || undefined,
    evaluationRationale: fm.evaluation_rationale || undefined,
    evaluationStatus: fm.evaluation_status || undefined,
    evaluatedByModel: fm.evaluated_by_model || undefined,
    evaluatedAt: fm.evaluated_at || undefined,
    suggestedCategory: fm.suggested_category || undefined,
    category: fm.category || undefined,
  };
}
